import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { supplierPortalApi } from '@/api/supplierPortal'
import { AppShell } from '@/components/AppShell'
import { IconClipboard, IconDownload } from '@/components/icons'
import { Alert, Button, EmptyState, SkeletonList } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { formatDate } from '@/lib/format'

/**
 * Το δελτίο με το οποίο κατεβαίνει κανείς στην αποθήκη.
 *
 * Πρώτα το άθροισμα με μεγάλα γράμματα και μετά η ανάλυση ανά πελάτη: το
 * σύνολο απαντά στο «πόσα να φορτώσω», η ανάλυση στο «σε ποιον πάει τι».
 */
export default function PickingListPage() {
  const { t } = useTranslation()
  const translateError = useApiError()
  const [date, setDate] = useState(today())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const downloadPdf = useMutation({
    mutationFn: () => supplierPortalApi.downloadPickingList(date),
  })

  const sheet = useQuery({
    queryKey: ['picking-list', date],
    queryFn: () => supplierPortalApi.pickingList(date),
  })

  const toggle = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <AppShell title={t('picking.title')}>
      <p className="mb-3 text-sm text-gray-500">{t('picking.hint')}</p>

      <section className="mb-4 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
          {t('picking.date')}
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-gray-900"
          />
        </label>

        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setDate(today())}>
            {t('picking.today')}
          </Button>
          <Button variant="secondary" onClick={() => setDate(tomorrow())}>
            {t('picking.tomorrow')}
          </Button>
        </div>
      </section>

      {sheet.isPending && <SkeletonList rows={4} />}
      {sheet.isError && <Alert>{translateError(sheet.error)}</Alert>}

      {sheet.data && (
        <>
          <div className="mb-4 rounded-2xl border border-brand-200 bg-brand-50 p-4">
            <p className="text-sm font-semibold text-brand-900">
              {t('picking.summary', {
                orders: sheet.data.orderCount,
                customers: sheet.data.customerCount,
                items: sheet.data.itemCount,
              })}
            </p>
            {sheet.data.undatedIncluded && (
              <p className="mt-1 text-xs text-brand-800">{t('picking.undatedIncluded')}</p>
            )}
            <p className="mt-2 text-xs text-brand-800">{formatDate(sheet.data.date)}</p>
          </div>

          {sheet.data.itemCount === 0 ? (
            <EmptyState
              icon={<IconClipboard size={26} />}
              title={t('picking.empty')}
              description={t('picking.emptyHint')}
            />
          ) : (
            <>
              <ul className="mb-4 flex flex-col gap-2">
                {sheet.data.items.map((item) => {
                  const key = `${item.name}|${item.unit}`
                  const open = expanded.has(key)
                  return (
                    <li key={key} className="rounded-2xl border border-gray-200 bg-white">
                      <button
                        onClick={() => toggle(key)}
                        aria-expanded={open}
                        className="flex w-full items-center justify-between gap-3 p-4 text-left"
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-gray-900">
                            {item.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {item.lines.length} × {t('picking.breakdown').toLowerCase()}
                          </span>
                        </span>
                        <span className="shrink-0 text-lg font-bold text-brand-700">
                          {formatQuantity(item.totalQuantity)} {t(`units.${item.unit}`)}
                        </span>
                      </button>

                      {open && (
                        <ul className="border-t border-gray-100 px-4 py-2">
                          {item.lines.map((line) => (
                            <li
                              key={`${line.orderId}-${line.quantity}-${line.businessName}`}
                              className="flex items-start justify-between gap-3 py-1.5 text-sm"
                            >
                              <span className="min-w-0">
                                <span className="block truncate text-gray-700">
                                  {line.businessName}
                                </span>
                                <span className="font-mono text-xs text-gray-400">
                                  {line.orderNumber}
                                </span>
                                {line.note && (
                                  <span className="block text-xs italic text-amber-700">
                                    {line.note}
                                  </span>
                                )}
                              </span>
                              <span className="shrink-0 text-gray-600">
                                {formatQuantity(line.quantity)} {t(`units.${item.unit}`)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  )
                })}
              </ul>

              {/* Η εκτύπωση φεύγει από το ίδιο endpoint με τα ίδια δεδομένα:
                  ό,τι βλέπει κανείς στην οθόνη είναι ό,τι κρατά στο χέρι. */}
              <Button loading={downloadPdf.isPending} onClick={() => downloadPdf.mutate()}>
                <IconDownload size={19} />
                {t('picking.download')}
              </Button>
            </>
          )}
        </>
      )}
    </AppShell>
  )
}

function formatQuantity(value: string): string {
  const number = Number(value)
  return Number.isFinite(number) ? new Intl.NumberFormat('el-GR').format(number) : value
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function tomorrow(): string {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  return date.toISOString().slice(0, 10)
}
